import React, { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { Portal } from '@mui/material';
import { objectBrowserStoreSelector, useObjectBrowserStore } from '@warehouse/object-browser/domain';
import { TabPanel, TabsHeader, TabsWrapper } from '@warehouse/shared/ui';
import Icon from '@mdi/react';
import { mdiArrowLeftCircleOutline, mdiArrowRightCircleOutline } from '@mdi/js';
import { NxObjectType } from '@warehouse/object-browser/core';
import { useStateQuery } from '@warehouse/shared/util';
import NxLoader from '../../../../src/components/library/NxLoader';
import { GeneralTab } from './GeneralTab';
import { MediaInfoTab } from './MediaInfoTab';
import { LogsTab } from './LogsTab';
import { SideDrawerHeader, SideModal } from '../../../../src/components/library/SideDrawer';
import Button from '../../../../src/components/library/Button';

interface SelectedItemDrawerProps {
	wrapperRef?: React.RefObject<HTMLDivElement>;
}

export function SelectedItemDrawer({ wrapperRef }: SelectedItemDrawerProps) {
	const isNxObjectDrawerOpen = useObjectBrowserStore(objectBrowserStoreSelector.isNxObjectDrawerOpen);
	const actions = useObjectBrowserStore(objectBrowserStoreSelector.actions);
	const selectedNxObject = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObject);
	const selectedNxObjectFull = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectFull);
	const isSelectedNxObjectFirst = useObjectBrowserStore(objectBrowserStoreSelector.isSelectedNxObjectFirst);
	const isSelectedNxObjectLast = useObjectBrowserStore(objectBrowserStoreSelector.isSelectedNxObjectLast);
	const isSelectedItemPresent = useObjectBrowserStore(objectBrowserStoreSelector.isSelectedItemPresent);
	const selectedNxObjectUuid = useObjectBrowserStore(objectBrowserStoreSelector.selectedNxObjectUuid);

	const [selectedTab, setSelectedTab] = useStateQuery<string>({
		queryName: 'selectedNxObjectDrawerTab',
		type: 'string',
	});

	const [, setSelectedNxObjectUuid] = useStateQuery<string>({
		queryName: 'selectedNxObjectUuid',
		type: 'string',
	});

	const tabs = useMemo(
		() => [
			{ label: 'General', component: <GeneralTab /> },
			...(selectedNxObject?.itemType === NxObjectType.File
				? [{ label: 'MediaInfo', component: <MediaInfoTab /> }]
				: []),
			{ label: 'Logs', component: <LogsTab /> },
		],
		[selectedNxObject],
	);

	const selectedTabIndex = Math.max(
		0,
		tabs.findIndex((t) => t.label.toLowerCase() === selectedTab?.toLowerCase()),
	);
	useEffect(() => {
		if (!isNxObjectDrawerOpen) return;

		const urlTab = new URLSearchParams(window.location.search).get('selectedNxObjectDrawerTab');
		const matchedTab = tabs.find((tab) => tab.label.toLowerCase() === urlTab?.toLowerCase());

		setSelectedTab(matchedTab?.label ?? 'General');
		setSelectedNxObjectUuid(selectedNxObjectUuid ?? '');
	}, [isNxObjectDrawerOpen, selectedNxObjectUuid, setSelectedNxObjectUuid, setSelectedTab, tabs]);

	return (
		<Portal container={wrapperRef?.current || document.body}>
			<SideModal
				maxWidth={900}
				side="right"
				open={isNxObjectDrawerOpen || !!selectedTab}
				handleClose={() => {
					actions.closeNxObjectDrawer();
					setSelectedTab(null);
					setSelectedNxObjectUuid(null);
				}}
				header={
					<SideDrawerHeader
						maxWidth="900px"
						title={selectedNxObjectFull?.nxObject?.name || selectedNxObject?.name}
						handleClose={() => {
							actions.closeNxObjectDrawer();
							setSelectedTab(null);
							setSelectedNxObjectUuid(null);
						}}
						action={
							isSelectedItemPresent ? (
								<NextPreviousWrapper>
									<Button
										nxstyle="tertiary-light"
										onClick={() => {
											actions.selectPreviousObject();
										}}
										disabled={isSelectedNxObjectFirst}
									>
										<Icon aria-label="Select Previous Object Drawer" path={mdiArrowLeftCircleOutline} size={1} />
									</Button>
									<Button
										nxstyle="tertiary-light"
										onClick={() => {
											actions.selectNextObject();
										}}
										disabled={isSelectedNxObjectLast}
									>
										<Icon aria-label="Select Next Object Drawer" path={mdiArrowRightCircleOutline} size={1} />
									</Button>
								</NextPreviousWrapper>
							) : null
						}
					/>
				}
			>
				{!selectedNxObject ? (
					<DrawerLoaderWrapper>
						<NxLoader size={60} />
					</DrawerLoaderWrapper>
				) : (
					<TabsWrapper>
						<TabsHeader
							selectedTab={selectedTabIndex}
							onChange={(index) => {
								setSelectedTab(tabs[index]?.label ?? null);
							}}
							prefix="object-browser-drawer"
							tabs={tabs}
						/>
						{tabs.map((tab, index) => (
							<TabPanel prefix="object-browser-drawer" key={index} value={selectedTabIndex} index={index}>
								<PaddingWrapper>{tab.component}</PaddingWrapper>
							</TabPanel>
						))}
					</TabsWrapper>
				)}
			</SideModal>
		</Portal>
	);
}

const NextPreviousWrapper = styled.div(
	() => css`
		align-items: center;
		display: flex;
		justify-content: space-between;
		width: min-content;
	`,
);

const PaddingWrapper = styled.div(
	({ theme }) => `
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		overflow-y: auto;
		overflow-x: hidden;
		height: calc(100vh - 128px);
		padding: ${theme.spacing(3)};
		gap: ${theme.spacing(2)};
		width: 900px;
		box-sizing: border-box;
	`,
);

const DrawerLoaderWrapper = styled.div(
	() => css`
		align-items: center;
		background-color: #ffffff;
		box-sizing: border-box;
		display: flex;
		height: calc(100vh - 128px);
		justify-content: center;
		max-width: 100%;
		width: 900px;
	`,
);
